import {
    LEVEL_PROGRESS_GREEN_COLOR,
    LEVEL_PROGRESS_GREY_COLOR,
    LEVEL_PROGRESS_LIGHT_GREEN_COLOR,
    LEVEL_PROGRESS_SPEED_PLAY_COMPLETE,
} from '@utils/const/level_progress';
import {useTheme} from '@components/theme/useTheme';
import {useCallback} from 'react';

// Helper to blend two rgba/hex colors with white (for completed bars)
function blendWithWhite(color: string, ratio: number): string {
    // Only supports rgba/hex, not named colors
    if (color.startsWith('#')) {
        // hex to rgb
        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const blend = (c: number) => Math.round(c * (1 - ratio) + 255 * ratio);
        return `rgb(${blend(r)}, ${blend(g)}, ${blend(b)})`;
    } else if (color.startsWith('rgba')) {
        const match = color.match(
            /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/
        );
        if (!match) return color;
        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);
        const a = match[4] !== undefined ? parseFloat(match[4]) : 1;
        const blend = (c: number) => Math.round(c * (1 - ratio) + 255 * ratio);
        return `rgba(${blend(r)}, ${blend(g)}, ${blend(b)}, ${a})`;
    }
    return color;
}

/**
 * Custom hook to generate a color based on progress value (0-100) and theme.
 * Uses LEVEL_PROGRESS_* constants. Completed bars are lightened.
 */
export function useGenerateBorderColor(): (
    complete: number,
    speedPlayPercent: number
) => string {
    const theme = useTheme(); // assign theme to a variable for future use
    return useCallback(
        (complete: number, speedPlayPercent: number): string => {
            if (speedPlayPercent === 100) {
                return LEVEL_PROGRESS_SPEED_PLAY_COMPLETE;
            }
            if (complete < 20) {
                return LEVEL_PROGRESS_GREY_COLOR;
            } else if (complete < 70) {
                return LEVEL_PROGRESS_LIGHT_GREEN_COLOR;
            } else if (complete <= 100) {
                // Lighten the green for completed bars
                return theme === 'dark'
                    ? blendWithWhite(
                          LEVEL_PROGRESS_GREEN_COLOR,
                          complete === 100 ? 0.5 : 0
                      )
                    : LEVEL_PROGRESS_GREEN_COLOR;
            }
            return LEVEL_PROGRESS_GREY_COLOR;
        },
        [theme]
    ); // add theme as a dependency for future-proofing
}
